import { QuoteResultDto } from 'apiConnector/quote/dto/quoteResultDto';
import { SmartQuotePromptsResponse } from 'apiConnector/smart-quote/model';
import { IdType } from 'epic/common-form/ReloadFormRequest';
import { StaticFormResult } from 'models/forms/StaticFormResult';
import { RefObject } from 'react';
import { batchFormQuoteReloadRequested } from 'state/batchForm/batchForm.action';
import { commonQuoteAction } from 'state/forms/actions/quote';
import { quotesRemoved } from 'state/quotes/quotes.actions';
import { getBatchTabIdToReloadForm } from 'state/selectors';
import { Thunk } from 'state/state.common';
import { batchWorkspaceProductSelectedThunk } from 'state/workspaces/batch/tabContent/tabContent.thunks';
import { smartQuoteActions } from 'state/workspaces/smart-quote/actions';
import { SmartQuoteParsingResult, SmartQuotePrompt } from 'state/workspaces/smart-quote/model';
import { getTabQuotesId } from 'state/workspaces/smart-quote/selectors';
import {
  workspaceResetTabRequested,
  workspaceTabActivated,
  workspaceTabCreated,
} from 'state/workspaces/workspace.actions';
import { scrollPageToBottom } from 'utils/scroll';
import { NextSmartQuoteResponseEvent } from 'utils/streaming/smart-quote';
import { getToastUtils } from 'utils/toast';

export function handleQuoteResponseThunk(nextSmartQuoteResponse: NextSmartQuoteResponseEvent, tabId: string): Thunk {
  return (dispatch, _, { getNow }) => {
    dispatch(
      commonQuoteAction.quoteRequested(
        'smartQuote',
        tabId,
        'Indicative',
        getNow().getTime(),
        nextSmartQuoteResponse.rfqId,
      ),
    );
    dispatch(smartQuoteActions.quoteResponse(tabId, nextSmartQuoteResponse));
  };
}

export function handleQuotePriceResponseThunk(quoteResult: QuoteResultDto<StaticFormResult>, tabId: string): Thunk {
  return (dispatch, _, { getNow }) => {
    dispatch(commonQuoteAction.quoteReceived('smartQuote', tabId, quoteResult.quoteId, quoteResult, getNow()));
  };
}

export function handlePromptsFetchedThunk(tabId: string, promptsResponse: SmartQuotePromptsResponse): Thunk {
  return (dispatch, _) => {
    // map
    const prompts = promptsResponse.prompts.map(
      p =>
        ({
          ...p,
          status: 'Finished' as const,
        }) satisfies SmartQuotePrompt,
    );

    dispatch(smartQuoteActions.promptFetched(tabId, prompts));
  };
}

export function resetTabRequestedThunk(tabId: string): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const quotesId = getTabQuotesId(state, tabId);

    dispatch(workspaceResetTabRequested(tabId, 'smartQuote'));
    if (quotesId.length > 0) {
      dispatch(quotesRemoved(tabId, ...quotesId));
    }
  };
}

export function openResultsInBatchWorkspaceThunk(results: SmartQuoteParsingResult[]): Thunk {
  return (dispatch, getState, { getNewGuid, navigateTo }) => {
    const validResults = results.filter(
      result =>
        !!result.rfqId &&
        !!result.productKey?.instrument?.name &&
        !!result.productKey?.instrument?.contractName &&
        !!result.productKey?.wrapper,
    );

    if (validResults.length === 0) {
      return;
    }

    const productType = validResults[0].productKey.instrument.name;
    const productSubType = validResults[0].productKey.instrument.contractName;
    const wrapper = validResults[0].productKey.wrapper;

    const state = getState();
    const matchedTabId = getBatchTabIdToReloadForm(state, productType, productSubType, wrapper);
    const tabId = matchedTabId ?? getNewGuid();

    if (!matchedTabId) {
      dispatch(workspaceTabCreated(tabId, 'newBatch', 'quote'));
      dispatch(
        batchWorkspaceProductSelectedThunk(tabId, productType, productSubType, wrapper, undefined, undefined, true),
      );
    }

    dispatch(workspaceTabActivated(tabId, 'newBatch', 'quote'));

    if (typeof navigateTo === 'function') {
      navigateTo('/batch');
    }

    validResults.forEach(({ rfqId }) => {
      dispatch(
        batchFormQuoteReloadRequested(
          tabId,
          getNewGuid(),
          rfqId,
          IdType.QuoteId,
          productType,
          productSubType,
          wrapper,
          rfqId,
          undefined,
          'ExoOne',
        ),
      );
    });

    const toast = getToastUtils(dispatch);
    toast.success(
      {
        id: 'openInBatchWorkspace.toast.content',
        values: { nbOfQuotes: validResults.length },
      },
      { id: 'openInBatchWorkspace' },
    );
  };
}

export function addClientCheckStepThunk(tabId: string, email: File, pageRef: RefObject<HTMLDivElement | null>): Thunk {
  return (dispatch, _, { getNewGuid }) => {
    const promptId = getNewGuid();
    const reader = new FileReader();

    reader.onload = () => {
      const encodedFile = reader.result as string;
      dispatch(smartQuoteActions.addClientCheckStep(tabId, promptId, email.name, encodedFile));
      scrollPageToBottom(pageRef);
    };
    reader.readAsDataURL(email);
  };
}
